import { useEffect } from 'react';
import { Barlow, Orbitron } from 'next/font/google';
import { useRouter } from 'next/router';

import MainLayout from '@/components/shared/main-layout/MainLayout';
import SpinnerLayout from '@/DesignSystem/spinner/spinner-layout';
import { Provider } from '@/context';
import Head from 'next/head';
import { Box } from '@mui/material';
import '@/styles/base.scss';
import PrismaMainLayout from '@/Prisma/PrismaMainLayout';
import useIsPrismaPage from '@/DesignSystem/hooks/useIsPrismaPage';

// Configuración global de prefetch
if (typeof window !== 'undefined') {
    // Desactivar prefetch por defecto para todos los Links
    const originalPrefetch = window.__NEXT_DATA__?.router?.prefetch;
    if (originalPrefetch) {
        window.__NEXT_DATA__.router.prefetch = () => {
            // No hacer nada - prefetch desactivado
        };
    }
}

const barlow = Barlow({
    subsets: ['latin'],
    weight: ['400', '500', '600', '700'],
    display: 'swap',
    preload: true,
});

const orbitron = Orbitron({
    weight: ['600', '700', '800', '900'],
    subsets: ['latin'],
    display: 'swap',
    preload: true,
    variable: '--font-orbitron',
});

export default function App({ Component, pageProps }) {
    const router = useRouter();
    const isBettingLanding = router.pathname?.includes('/apuestas-deportivas/web/');

    // ✅ Service Worker registration optimizado
    useEffect(() => {
        if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

        const isProd = process.env.NEXT_PUBLIC_ENV_VARIABLE !== 'development';

        // ✅ Helper para registrar SW con manejo de errores mejorado
        const registerSW = async (swPath, options = {}) => {
            try {
                const existingSW = await navigator.serviceWorker.getRegistration(swPath);

                if (!existingSW) {
                    const registration = await navigator.serviceWorker.register(swPath, {
                        updateViaCache: 'none',
                        scope: '/',
                        ...options,
                    });

                    if (swPath === '/sw.js' && registration) {
                        // Solo para PWA SW: listener de updates
                        registration.addEventListener('updatefound', () => {
                            console.log('🔄 Nueva versión del SW disponible');

                            // Detectar si estamos en una ruta dinámica
                            const isDynamicRoute =
                                /^\/(equipo|partido|jugadores|estadisticas-fantasy)\/.*/.test(
                                    window.location.pathname
                                );

                            if (isDynamicRoute) {
                                // Si hay una nueva versión y estamos en una ruta dinámica,
                                // esperar a que el nuevo SW esté activo y recargar
                                const newWorker = registration.installing || registration.waiting;
                                if (newWorker) {
                                    newWorker.addEventListener('statechange', () => {
                                        if (newWorker.state === 'activated') {
                                            console.log(
                                                '🔄 Recargando página para aplicar nueva versión del SW'
                                            );
                                            window.location.reload();
                                        }
                                    });
                                }
                            }
                        });
                    }

                    console.log(`✅ SW registrado: ${swPath}`);
                    return registration;
                }

                return existingSW;
            } catch (error) {
                console.error(`❌ Error registrando SW ${swPath}:`, error);
                return null;
            }
        };

        // ✅ Helper para verificar updates (solo cada 24h)
        const checkSWUpdate = async (swPath) => {
            try {
                const existingSW = await navigator.serviceWorker.getRegistration(swPath);
                if (!existingSW) return;

                const lastCheck = localStorage.getItem(`sw-last-check-${swPath}`);
                const now = Date.now();
                const oneDay = 24 * 60 * 60 * 1000;

                if (!lastCheck || now - parseInt(lastCheck) > oneDay) {
                    const registration = await existingSW.update();
                    localStorage.setItem(`sw-last-check-${swPath}`, now.toString());
                    console.log(`🔄 SW actualizado: ${swPath}`);

                    // Si hay una actualización disponible y estamos en una ruta dinámica
                    if (registration && registration.waiting) {
                        const isDynamicRoute = /^\/(equipo|partido|jugadores|estadisticas-fantasy)\/.*/.test(
                            window.location.pathname
                        );

                        if (isDynamicRoute) {
                            // Escuchar cuando el nuevo SW esté activo
                            registration.waiting.addEventListener('statechange', () => {
                                if (registration.waiting?.state === 'activated') {
                                    console.log('🔄 Recargando página para aplicar nueva versión del SW');
                                    window.location.reload();
                                }
                            });

                            // Si el SW ya está activo, recargar inmediatamente
                            if (registration.active && registration.waiting) {
                                console.log('🔄 Nueva versión del SW lista, recargando...');
                                window.location.reload();
                            }
                        }
                    }
                }
            } catch (error) {
                console.warn(`⚠️ Error actualizando SW ${swPath}:`, error);
            }
        };

        // ✅ Detectar si hay un SW esperando y estamos en ruta dinámica
        const checkWaitingSW = async () => {
            try {
                const registration = await navigator.serviceWorker.getRegistration('/sw.js');
                if (!registration || !registration.waiting) return;

                const isDynamicRoute = /^\/(equipo|partido|jugadores|estadisticas-fantasy)\/.*/.test(
                    window.location.pathname
                );

                if (isDynamicRoute && registration.waiting) {
                    console.log('🔄 SW esperando activación en ruta dinámica, recargando...');
                    // Forzar activación del nuevo SW
                    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                    // Recargar después de un breve delay
                    setTimeout(() => {
                        window.location.reload();
                    }, 100);
                }
            } catch (error) {
                console.warn('⚠️ Error verificando SW esperando:', error);
            }
        };

        // ✅ Registro secuencial para evitar race conditions
        const initServiceWorkers = async () => {
            if (isProd) {
                // Producción: Registrar PWA SW primero
                const pwaSW = await registerSW('/sw.js');

                // Verificar si hay un SW esperando (puede pasar si el usuario vuelve después de una actualización)
                await checkWaitingSW();

                // Verificar updates solo si ya existía
                if (pwaSW && pwaSW.active) {
                    await checkSWUpdate('/sw.js');
                }

                // ⚠️ Firebase messaging SW deshabilitado temporalmente
                // await registerSW('/firebase-messaging-sw.js');
            }
            // ⚠️ Firebase messaging SW deshabilitado en desarrollo también
        };

        // ✅ Ejecutar con delay para no bloquear la carga inicial
        const timer = setTimeout(() => {
            initServiceWorkers();
        }, 1000); // 1 segundo de delay

        return () => clearTimeout(timer);
    }, []);

    const isPrismaPage = useIsPrismaPage();

    // Si es la landing de apuestas, no usar layout de Analítica Fantasy
    if (isBettingLanding) {
        return <Component {...pageProps} />;
    }

    // Si es una página de Prisma, usar el layout específico
    if (isPrismaPage) {
        return (
            <Box component={'section'} className={`prisma-site ${barlow.className} ${orbitron.variable}`}>
                <PrismaMainLayout>
                    <Component {...pageProps} />
                </PrismaMainLayout>
            </Box>
        );
    }

    return (
        <Box
            component={'section'}
            className={`analitica-fantasy-site ${barlow.className} ${orbitron.variable}`}
        >
            <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
            </Head>
            <Provider>
                <MainLayout>
                    <SpinnerLayout>
                        <Component {...pageProps} />
                    </SpinnerLayout>
                </MainLayout>
            </Provider>
        </Box>
    );
}
